import { controlAnswerKey } from "./regulatoryIdentity"
import { contextCategoryPoints } from "./regulatoryCategories"
import { RegulatoryControlAnswer } from "../generated/proto-ts/main"
import { ICloudRegulatoryControlPoint, IEN1176AnswerMap } from "../types"
export type PrintableControl = ICloudRegulatoryControlPoint & {
  printAnswer?: RegulatoryControlAnswer
  printContinuation?: boolean
}
// Split unusually long cards before measuring, preserving every character and
// the original answer/control identities. Continuations are presentation only.
export function splitPrintControls(
  points: ICloudRegulatoryControlPoint[],
  answers: IEN1176AnswerMap,
): PrintableControl[] {
  return contextCategoryPoints(points).flatMap((point) => {
    const answer = (point as PrintableControl).printAnswer ?? answers[controlAnswerKey(point)]
    const definition = point.Definition
    const fields: { kind: "definition" | "answer"; key: string; value: string }[] = []
    for (const key of [
      "nature",
      "applicability",
      "method",
      "expected",
      "unit",
      "sourceFile",
      "interpretationNote",
      "missingPart",
    ])
      if (typeof definition?.[key] === "string" && definition[key])
        fields.push({ kind: "definition", key, value: definition[key] as string })
    for (const key of [
      "comment",
      "measurement",
      "recommendation",
      "immediate_action",
      "photo_exception",
    ] as const)
      if (answer?.[key]) fields.push({ kind: "answer", key, value: answer[key] })
    if (
      fields.reduce((n, f) => n + f.value.length + (f.value.match(/\n/g)?.length ?? 0) * 80, 0) <=
      1300
    )
      return [point]
    const chunks: (typeof fields)[] = [[]]
    let used = 0
    for (const field of fields) {
      // Count newlines as occupied lines even when their strings are short.
      const parts = field.value.match(/[^\n]{1,500}|\n/gu) ?? []
      for (const value of parts) {
        const cost = Math.max(value.length, value === "\n" ? 80 : 0)
        if (used + cost > 700 && chunks[chunks.length - 1]!.length) {
          chunks.push([])
          used = 0
        }
        chunks[chunks.length - 1]!.push({ ...field, value })
        used += cost
      }
    }
    return chunks.map((chunk, index) => {
      const p = { ...point, printContinuation: index > 0 } as PrintableControl
      if (definition) {
        p.Definition = { ...definition }
        for (const field of fields.filter((f) => f.kind === "definition"))
          p.Definition[field.key] = ""
      }
      const a = answer?.clone() ?? new RegulatoryControlAnswer()
      for (const key of [
        "comment",
        "measurement",
        "recommendation",
        "immediate_action",
        "photo_exception",
      ] as const)
        a[key] = ""
      if (index > 0) {
        a.severity = ""
        a.due_date = ""
        a.pictures = []
      }
      for (const field of chunk) {
        if (field.kind === "definition" && p.Definition)
          p.Definition[field.key] = String(p.Definition[field.key] ?? "") + field.value
        else {
          const key = field.key as "comment"
          a[key] += field.value
        }
      }
      p.printAnswer = a
      return p
    })
  })
}
export function packMeasuredCards<T>(
  cards: T[],
  heights: number[],
  firstHeight: number,
  nextHeight: number,
): T[][] {
  if (!cards.length) return []
  const pages: T[][] = [[]]
  let available = firstHeight
  cards.forEach((card, index) => {
    const height = heights[index] ?? nextHeight
    if (height > available) {
      pages.push([])
      available = nextHeight
    }
    pages[pages.length - 1]!.push(card)
    available -= height
  })
  return pages
}
